import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { PhotoFilmstrip, PhotoMosaic, PhotoWatermark } from "../components/PhotoElements";
import { SHOW_PHOTOS } from "../lib/photos";

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 pb-24">
      {/* HERO */}
      <section className="relative h-[55vh] min-h-[400px] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(/images/about-hero.jpg)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-950/50 via-black/40 to-stone-950" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-16 z-10">
          <p className="font-overline text-xs text-[#DE9E48] mb-4">— About</p>
          <h1 className="font-display text-6xl md:text-8xl tracking-tighter leading-[0.95]">
            Our Story, <br />
            <span className="italic text-[#DE9E48]">our mission.</span>
          </h1>
        </div>
      </section>

      {/* MAIN COPY */}
      <section className="px-6 md:px-10 py-24 max-w-7xl mx-auto grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <h2 className="font-display text-4xl md:text-5xl leading-tight sticky top-32">
            &ldquo;The Little Theatre <br />
            <span className="italic text-[#DE9E48]">with A Lot of Soul.&rdquo;</span>
          </h2>
        </div>
        <div className="md:col-span-7 space-y-6 font-body text-stone-300 text-base md:text-lg leading-relaxed">
          <p>
            Encore Theatre and Gallery is a Birmingham-based community theatre
            and art gallery founded in 2018. While we believe that a rich and
            culturally diverse arts landscape requires all voices, Encore is
            steadfast in its mission to ensure that talented voices from the
            African diaspora are woven into that tapestry.
          </p>
          <p>
            With purpose and passion, we champion storytelling that reflects
            the beauty, resilience, and creativity of our community. Through
            innovative productions, impactful programs, and a commitment to
            representation, we aim to inspire, educate, and connect our city.
          </p>
          <p>
            We are a cornerstone of Birmingham — created by and for the people
            who live here. We exist to uplift powerful stories, cultivate local
            talent, and give young people a creative path forward. It is a
            space where families gather, where community grows, and where
            culture is preserved.
          </p>
          <p>
            Supporting Encore isn't about choosing sides. It's about investing
            in Birmingham's future — a future where more people are engaged,
            uplifted, and proud to call this city home.
          </p>
        </div>
      </section>

      {/* OUR JOURNEY — Editorial history */}
      <section className="py-24 md:py-32 px-6 md:px-10 border-t border-white/5 relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -left-40 w-[34rem] h-[34rem] rounded-full opacity-[0.05] blur-3xl"
          style={{ background: "radial-gradient(circle, #DE9E48 0%, transparent 70%)" }}
        />

        <div className="max-w-4xl mx-auto relative">
          <p className="font-overline text-xs text-[#DE9E48] mb-5">— Our Journey</p>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1.05] mb-4">
            About Encore Theatre <br />
            <span className="italic text-[#DE9E48]">and Gallery.</span>
          </h2>
          <p className="font-overline text-[0.7rem] text-stone-400 mb-14 tracking-[0.22em]">
            Also operating as Encore School of Arts and Theatre
          </p>

          <div className="space-y-7 font-body text-stone-300 text-base md:text-lg leading-[1.75]">
            <p>
              Founded in 2018 by Birmingham-born playwright, director, and
              producer Marc Raby, Encore Theatre and Gallery was established
              with a singular vision: to create a cultural home where Black
              stories, voices, and artistry could be seen, celebrated, and
              preserved through the transformative power of live performance.
            </p>
            <p>
              What began as an ambitious grassroots theatre company quickly
              evolved into one of Alabama&rsquo;s few Black-owned brick-and-mortar
              theatre institutions&mdash;a space devoted not only to performance,
              but to education, artistic development, and community engagement.
              Incorporated as the{" "}
              <span className="text-stone-100">
                Encore School of Arts and Theatre
              </span>{" "}
              in 2019, the organization expanded its mission to include youth
              arts education, actor training, new works development, and
              interdisciplinary programming that bridges theatre, music, and
              visual art.
            </p>
            <p>
              Over the years, Encore has become known for producing bold
              contemporary works, classic dramas, musicals, original plays, and
              culturally resonant storytelling rooted in the African-American
              experience. Guided by its mantra,
            </p>

            <blockquote className="border-l-2 border-[#DE9E48] pl-6 md:pl-8 my-10 font-display italic text-2xl md:text-3xl text-stone-50 leading-snug">
              &ldquo;The Little Theatre with a Lot of Soul,&rdquo;
            </blockquote>

            <p>
              Encore has built a reputation for intimate, emotionally rich
              productions that challenge, entertain, and inspire.
            </p>
            <p>
              From its early beginnings in East Birmingham to its{" "}
              <span className="text-stone-100">2025 relocation</span> into the
              heart of downtown Birmingham, Encore&rsquo;s journey has mirrored
              its mission&mdash;growing with intention, resilience, and purpose.
              Its current home in Birmingham&rsquo;s historic theatre district
              places it among the city&rsquo;s cultural landmarks, positioning
              the organization as an essential contributor to the region&rsquo;s
              artistic ecosystem.
            </p>
            <p>
              More than a theatre, Encore serves as a creative incubator for
              emerging and established artists alike. Through programs like the
              Encore School of Arts and Theatre, youth camps, playwright
              development initiatives, and community collaborations, the
              organization invests in the next generation of storytellers while
              creating meaningful artistic opportunities for today.
            </p>
            <p>
              At its core, Encore exists to ensure that stories from the African
              diaspora are not marginalized, but{" "}
              <span className="text-[#DE9E48] italic">centered</span>. In an
              ever-changing cultural landscape, the organization remains
              committed to producing work that is fearless, inclusive, and
              deeply human&mdash;art that reflects the beauty, complexity,
              humor, and truth of Black life.
            </p>

            <div className="pt-6 border-t border-[#DE9E48]/20 mt-12">
              <p className="font-display text-2xl md:text-3xl italic leading-snug text-stone-50">
                As Encore continues to grow, its mission remains the same:
              </p>
              <p className="font-display text-xl md:text-2xl italic leading-snug text-[#DE9E48] mt-3">
                to amplify voices, preserve culture, educate artists, and
                create unforgettable theatrical experiences for Birmingham and
                beyond.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PHOTO FILMSTRIP — a pulse of past productions bridging hero to story */}
      <PhotoFilmstrip tone="duotone" speed={90} />

      {/* MOMENTS — photo mosaic of Encore productions */}
      <section className="relative py-24 md:py-32 px-6 md:px-10 overflow-hidden">
        <PhotoWatermark index={7} position="right" opacity={0.13} />
        <div className="max-w-7xl mx-auto relative">
          <div className="max-w-2xl mb-12 md:mb-14">
            <p className="font-overline text-[0.7rem] tracking-[0.3em] text-[#DE9E48] mb-4">
              — Moments
            </p>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.98]">
              Where the work{" "}
              <span className="italic text-[#DE9E48]">lives.</span>
            </h2>
            <p className="font-body text-stone-300 mt-5 leading-relaxed">
              A little of what happens on our stage — songs, silences, standing
              ovations — pulled from recent runs.
            </p>
          </div>
          <PhotoMosaic photos={SHOW_PHOTOS.slice(6, 12)} />
        </div>
      </section>

      {/* DIRECTOR */}
      <section className="py-32 px-6 md:px-10 spotlight relative overflow-hidden">
        <PhotoWatermark index={2} position="bottom-right" opacity={0.1} />
        <div className="max-w-5xl mx-auto text-center">
          <Quote size={42} className="text-[#DE9E48] mx-auto mb-8 opacity-60" />
          <blockquote className="font-display text-3xl md:text-4xl italic leading-snug">
            "Thank you for joining us in celebrating inclusion, empowerment,
            and the power of storytelling. Together, we are shaping a brighter,
            more connected future. Welcome to Encore."
          </blockquote>
          <p className="font-overline text-xs text-stone-400 mt-10">
            — Marc Raby, Artistic Director
          </p>
          <div className="mt-12">
            <Link to="/contact" className="btn-gold" data-testid="about-contact-cta">
              Get In Touch <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
